import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {LoadingViewState, RouterView} from "../RouterView";
import {
    BonusStatus,
    BonusTrigger,
    CurrencyDefinition,
    DepositRewardConfig,
    DepositRewardType,
    RewardTemplate,
} from "../../http/protocol";
import {Form, FormListener} from "../form/Form";
import {getContainer} from "../../ioc/IOCSetup";
import {FormGroup} from "../form/FormGroup";
import {FormInput} from "../form/FormInput";
import {FormTextArea} from "../form/FormTextArea";
import {FormSubmitError, FormSubmitSuccess} from "../form/FormFeedback";
import {Submit} from "../form/Submit";
import {Validators} from "../form/Validators";
import {OfferRuleService} from "../../services/OfferRuleService";
import {SelectInput} from "../form/SelectInput";
import {FormIfNot} from "../form/Switch";
import {DepositRewardConfigService} from "../../services/DepositRewardConfigService";
import {RewardsConfigService} from "../../services/RewardsConfigService";
import {DateRange} from "./EditBonusConfig";
import {ApplicationState} from "../../user/UserStore";
import {connect} from "react-redux";

interface Props extends RouteComponentProps {
    currencies?: CurrencyDefinition[];
}

interface State extends LoadingViewState {
    config?: DepositRewardConfig;
    validationMap?: any;
    offerRules?: BonusTrigger[];
    rewardTemplates?: RewardTemplate[];
}

class EditDepositRewardConfigView extends RouterView<Props, State> implements FormListener<DepositRewardConfig> {

    service: DepositRewardConfigService;
    offerRuleService: OfferRuleService;
    rewardsConfigService: RewardsConfigService;

    constructor(props: Props) {
        super(props);
        this.service = getContainer().getDepositRewardConfigService();
        this.offerRuleService = getContainer().getOfferRuleService();
        this.rewardsConfigService = getContainer().getRewardsConfigService();
        // The valid-date pickers render in UTC (DateRange's `utc` prop), so seed defaults at UTC
        // day boundaries — otherwise a local-midnight seed shows as the previous day in the picker.
        const now = new Date();
        const validFromDate = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);
        const validToDate = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 30, 23, 59, 0, 0);
        this.state = {config: {status: BonusStatus.DISABLED, rewardType: DepositRewardType.FREESPINS, validFromDate: validFromDate as any, validToDate: validToDate as any, autoClaim: true}, validationMap: {}, offerRules: [], rewardTemplates: []};
    }

    isNew = () => (this.props.match.params as any).id == "new";

    getTitle(): string {
        return this.isNew() ? "Create Deposit Reward Config" : "Edit Deposit Reward Config";
    }

    componentDidUpdate(prevProps: Props) {
        if ((prevProps.match.params as any).id != (this.props.match.params as any).id) {
            this.refreshContent();
        }
    }

    getCurrencyLabel(code: string) {
        if (this.props.currencies) {
            const c = this.props.currencies.find(c => c.code == code);
            if (c) {
                return c.name + " (" + c.code + ")";
            }
        }
        return "";
    }

    getTemplatesForType(type?: string): RewardTemplate[] {
        if (!type || !this.state.rewardTemplates) {
            return [];
        }
        return this.state.rewardTemplates.filter(t => t.rewardType === type.toLowerCase());
    }

    async loadContent(): Promise<any> {
        const [offerRulesResp, templatesResp] = await Promise.all([
            this.offerRuleService.list({offset: 0, limit: 500, orderBy: "name"}),
            this.rewardsConfigService.list({offset: 0, limit: 500}),
        ]);
        this.setState({offerRules: offerRulesResp.items, rewardTemplates: templatesResp.items || []});

        if (!this.isNew()) {
            const config = await this.service.getEntity((this.props.match.params as any).id);
            this.setState({config});
        }
    }

    onSubmit() {
        if (!this.state.config) return;
        if (!this.state.config.validFromDate || !this.state.config.validToDate) {
            return Promise.reject("Missing valid from/to date(s)");
        }
        return this.service.save(this.state.config).then((s: DepositRewardConfig) => {
            this.setState({config: s});
        }).catch(() => {
            return Promise.reject("Failed to save deposit reward config");
        });
    }

    formDidUpdate(formModel: DepositRewardConfig, validationMap?: any) {
        this.setState({config: formModel, validationMap: validationMap});
    }

    setFromDate(d: number) {
        this.setState({config: {...this.state.config, validFromDate: d as any}});
    }

    setToDate(d: number) {
        this.setState({config: {...this.state.config, validToDate: d as any}});
    }

    renderContent() {
        if (!this.state.config) {
            return <span></span>;
        }
        return (
            <div>
                <div className="wrapper wrapper-content animated fadeInRight">
                    <Form className="form form-horizontal" validationMap={this.state.validationMap} modelObject={this.state.config} formListener={this}>
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card-box">
                                    <div className="ibox-content">
                                        <h4 className={"ibox-title"}>General</h4>

                                        {!this.isNew() && (
                                            <span>
                                                <div className="hr-line-dashed"></div>
                                                <FormGroup className="form-group">
                                                    <label className="col-sm-2 control-label">ID</label>
                                                    <div className="col-sm-10">
                                                        <FormInput model="id" type="integer" className="form-control" disabled={true}/>
                                                    </div>
                                                </FormGroup>
                                            </span>
                                        )}

                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Name</label>
                                            <div className="col-sm-10">
                                                <FormInput model="name" validators={[Validators.required()]} type="text" className="form-control"/>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Description</label>
                                            <div className="col-sm-10">
                                                <FormTextArea model="description" className="form-control" rows={3}/>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Image URL</label>
                                            <div className="col-sm-10">
                                                <FormInput model="imageUrl" type="text" className="form-control"/>
                                                <span className="help" style={{display: "block", marginTop: 4}}>Image shown to players in the bonus offers list. Use the image URL from the reward template.</span>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Status</label>
                                            <div className="col-sm-10">
                                                <SelectInput model="status" values={[BonusStatus.ENABLED, BonusStatus.DISABLED]} className="form-control"/>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Valid from/to dates (UTC)</label>
                                            <div className="col-sm-10">
                                                <DateRange fromDate={this.state.config.validFromDate} toDate={this.state.config.validToDate}
                                                           setFromDate={(d) => this.setFromDate(d)} setToDate={(d) => this.setToDate(d)}/>
                                                <span className="help" style={{display: "block", marginTop: 4}}>The deposit reward offer will only be triggered during this active period. Deposits made outside these dates will not generate reward offers.</span>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Expires in (Days)</label>
                                            <div className="col-sm-10">
                                                <FormInput model="expiresAfterDays" validators={[Validators.required()]} type="text" className="form-control"/>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Auto Claim Reward</label>
                                            <div className="col-sm-10">
                                                <FormInput model="autoClaim" type="checkbox" className=""/>
                                                <span className="help" style={{display: "block", marginTop: 4}}>Recommended. When enabled, the reward (e.g. free spins) is activated immediately when the player claims the offer. When disabled, the player must also manually claim the reward in their Rewards tab.</span>
                                            </div>
                                        </FormGroup>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-box">
                            <div className="ibox-content">
                                <h4 className={"ibox-title"}>Trigger</h4>
                                <div className="hr-line-dashed"></div>
                                <FormGroup className="form-group">
                                    <label className="col-sm-2 control-label">Trigger</label>
                                    <div className="col-sm-10">
                                        <SelectInput values={this.state.offerRules}
                                                     convertOptionValueToModel={(id) => {
                                                         if (this.state.offerRules) {
                                                             return this.state.offerRules.find(m => m.id == id);
                                                         } else {
                                                             return "N/A";
                                                         }
                                                     }}
                                                     optionModelValue={(m) => m.id}
                                                     convertModelValueToOption={(m) => m.id}
                                                     displayValue={(mc: BonusTrigger) => "" + mc.name} model="trigger" className="form-control"/>
                                    </div>
                                </FormGroup>
                            </div>
                        </div>

                        <div className={"card-box"}>
                            <div className={"ibox-content"}>
                                <h4 className={"ibox-title"}>Reward</h4>
                                <div className="hr-line-dashed"></div>
                                <FormGroup className="form-group">
                                    <label className="col-sm-3 control-label">Reward Type</label>
                                    <div className="col-sm-9">
                                        <SelectInput model="rewardType" values={Object.keys(DepositRewardType)} className="form-control"/>
                                    </div>
                                </FormGroup>
                                <FormGroup className="form-group">
                                    <label className="col-sm-3 control-label">Reward Template</label>
                                    <div className="col-sm-9">
                                        {(() => {
                                            const config = this.state.config;
                                            const filtered = this.getTemplatesForType(config?.rewardType);
                                            return <select
                                                className="form-control"
                                                value={config?.rewardTemplateId ?? ""}
                                                onChange={(e) => {
                                                    const id = parseInt(e.target.value);
                                                    const selectedTemplate = this.state.rewardTemplates?.find(t => t.id == id);
                                                    this.setState({config: {
                                                        ...config,
                                                        rewardTemplateId: id,
                                                        currencyCode: selectedTemplate?.currencyCode || config?.currencyCode
                                                    }});
                                                }}
                                            >
                                                <option value="">Select template</option>
                                                {filtered.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name} (#{t.id})</option>
                                                ))}
                                            </select>;
                                        })()}
                                    </div>
                                </FormGroup>
                                <FormGroup className="form-group">
                                    <label className="col-sm-3 control-label">Currency Code</label>
                                    <div className="col-sm-9">
                                        {(() => {
                                            const config = this.state.config;
                                            const template = config?.rewardTemplateId
                                                ? this.state.rewardTemplates?.find(t => t.id == config.rewardTemplateId)
                                                : undefined;
                                            const isLocked = config?.useTemplateDefaults && template?.currencyCode;
                                            if (isLocked && config?.currencyCode !== template?.currencyCode) {
                                                setTimeout(() => this.setState({config: {...config, currencyCode: template?.currencyCode}}), 0);
                                            }
                                            const currencies = this.props.currencies || [];
                                            return <select
                                                className="form-control"
                                                disabled={!!isLocked}
                                                value={config?.currencyCode ?? ""}
                                                onChange={(e) => {
                                                    this.setState({config: {...config, currencyCode: e.target.value}});
                                                }}
                                            >
                                                <option value="">Select currency</option>
                                                {currencies.map(c => (
                                                    <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                                                ))}
                                            </select>;
                                        })()}
                                    </div>
                                </FormGroup>
                                <FormGroup className="form-group">
                                    <label className="col-sm-3 control-label">Use Template Defaults</label>
                                    <div className="col-sm-9">
                                        <FormInput model="useTemplateDefaults" type="checkbox" className="" style={{marginRight: 8}}/>
                                        <span className={"help"} style={{display: "block", marginTop: 4}}>When enabled, the reward uses the template's configured default amount and free spins instead of calculating from deposit percentage.</span>
                                    </div>
                                </FormGroup>
                                <FormGroup className="form-group">
                                    <label className="col-sm-3 control-label">Min Deposit Amount</label>
                                    <div className="col-sm-9">
                                        <FormInput model="minDepositAmount" validators={[Validators.required()]} type="text" className="form-control"/>
                                    </div>
                                </FormGroup>
                                <FormIfNot model="useTemplateDefaults" value={true}>
                                    <FormGroup className="form-group">
                                        <label className="col-sm-3 control-label">Deposit Percentage</label>
                                        <div className="col-sm-9">
                                            <FormInput model="depositPercentage" validators={[Validators.required()]} type="text" className="form-control"/>
                                            <span className={"help"} style={{display: "block", marginTop: 4}}>Fraction, e.g. 0.50 = 50%</span>
                                        </div>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label className="col-sm-3 control-label">Max Reward Amount</label>
                                        <div className="col-sm-9">
                                            <FormInput model="maxRewardAmount" validators={[Validators.required()]} type="text" className="form-control"/>
                                        </div>
                                    </FormGroup>
                                </FormIfNot>
                                {this.state.config?.rewardType === DepositRewardType.FREESPINS && !this.state.config?.useTemplateDefaults && (
                                    <div className="alert alert-info" style={{margin: "10px 0"}}>
                                        <strong>How free spins calculation works:</strong>
                                        <p style={{marginTop: 8, marginBottom: 4}}>
                                            The total reward amount is calculated as <em>deposit × percentage</em> (capped at max reward).
                                            This amount is then split into free spins using the <strong>reward template's</strong> default number of spins and default bet amount.
                                            Make sure the selected reward template has valid default values for both.
                                        </p>
                                        <ul style={{marginBottom: 4}}>
                                            <li>If the per-spin bet ends up <strong>at or above</strong> the template's default bet, the player gets the template's default number of spins at the higher bet.</li>
                                            <li>If the per-spin bet ends up <strong>below</strong> the template's default bet, the player gets fewer spins at the default bet, so the total stays within the reward amount.</li>
                                        </ul>
                                        <p style={{marginBottom: 4}}><strong>Examples</strong> (reward template: 10 spins, 2.00 EUR default bet):</p>
                                        <ul style={{marginBottom: 0}}>
                                            <li>Reward amount = 50 EUR → per-spin = 50/10 = 5.00 EUR ≥ 2.00 → <strong>10 spins × 5.00 EUR</strong></li>
                                            <li>Reward amount = 15 EUR → per-spin = 15/10 = 1.50 EUR &lt; 2.00 → <strong>7 spins × 2.00 EUR</strong> (= 14 EUR)</li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card-box">
                                    <FormSubmitError/>
                                    <FormSubmitSuccess text={"Saved successfully"}/>
                                    <div className="form-group">
                                        <div className="col-sm-12">
                                            <Submit className="btn btn-primary">Save</Submit>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: ApplicationState) => {
    return {currencies: state.settings.settings?.currencies};
}

export default connect(mapStateToProps)(withRouter(EditDepositRewardConfigView));